import { IcoPlusSmall16, IcoSettings24 } from '@onefootprint/icons';
import { useEffect, useRef, useState } from 'react';
import Button from '../../../button';
import { findDuplicateNames, generateId, hasDuplicateNamesDeep, type ParseResult, parseSchema, type SchemaModel, type SchemaProperty, serializeSchema } from '../../utils/schema-model';
import PropertyRow from './components/property-row';
type VisualEditorProps = {
  value: string;
  onChange: (value: string) => void;
  isDisabled?: boolean;
  /** Optional subtitle text shown in the empty state. */
  emptyStateSubtitle?: string;
};
const VisualEditor = ({
  value,
  onChange,
  isDisabled = false,
  emptyStateSubtitle
}: VisualEditorProps) => {
  const [model, setModel] = useState<SchemaModel | null>(() => {
    const result = parseSchema(value);
    return result.success ? result.model : null;
  });
  const [parseError, setParseError] = useState<ParseResult | null>(null);
  const modelRef = useRef(model);
  modelRef.current = model;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const prevPropertyCountRef = useRef(model?.properties.length ?? 0);
  useEffect(() => {
    const currentCount = model?.properties.length ?? 0;
    if (currentCount > prevPropertyCountRef.current) {
      scrollContainerRef.current?.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
    prevPropertyCountRef.current = currentCount;
  }, [model?.properties.length]);
  useEffect(() => {
    const result_0 = parseSchema(value);
    if (!result_0.success) {
      setParseError(result_0);
      return;
    }
    setParseError(null);
    if (modelRef.current) {
      const currentSerialized = serializeSchema(modelRef.current);
      if (currentSerialized === value) return;
    }
    setModel(result_0.model);
  }, [value]);
  const rootDuplicateNames = model ? findDuplicateNames(model.properties) : new Set<string>();
  const hasAnyDuplicates = model ? hasDuplicateNamesDeep(model) : false;
  const updateModel = (newModel: SchemaModel) => {
    setModel(newModel);
    if (!hasDuplicateNamesDeep(newModel)) {
      onChange(serializeSchema(newModel));
    }
  };
  const handlePropertyChange = (index: number, property: SchemaProperty) => {
    if (!model) return;
    const newProperties = [...model.properties];
    newProperties[index] = property;
    updateModel({
      properties: newProperties
    });
  };
  const handlePropertyDelete = (index_0: number) => {
    if (!model) return;
    updateModel({
      properties: model.properties.filter((_, i) => i !== index_0)
    });
  };
  const handleAddProperty = () => {
    if (!model) return;
    const existingNames = new Set(model.properties.map(p => p.name));
    let counter = model.properties.length + 1;
    let newName = `field_${counter}`;
    while (existingNames.has(newName)) {
      counter++;
      newName = `field_${counter}`;
    }
    updateModel({
      properties: [...model.properties, {
        id: generateId(),
        name: newName,
        type: 'string'
      }]
    });
  };
  if (parseError && !parseError.success) {
    const errorMessages: Record<typeof parseError.error, string> = {
      duplicateKeys: 'Duplicate keys detected in JSON. Remove duplicates to use the visual editor.',
      invalidJson: 'Invalid JSON syntax. Fix the JSON to use the visual editor.',
      invalidSchema: 'Schema must be an object type. Fix the JSON to use the visual editor.'
    };
    return <div className="flex flex-col items-center justify-center h-full p-6 text-center">
        <p className="text-body-2 text-tertiary">{errorMessages[parseError.error]}</p>
      </div>;
  }
  if (!model) {
    return <div className="flex flex-col items-center justify-center h-full p-6 text-center">
        <p className="text-body-2 text-tertiary">Unable to parse schema.</p>
      </div>;
  }
  return <div className="flex h-full flex-col overflow-hidden p-3 gap-4">
      <div ref={scrollContainerRef} className="flex min-h-0 flex-1 flex-col overflow-auto border-b border-tertiary">
        {hasAnyDuplicates && <p className="mb-3 text-caption-1 text-error">Duplicate property names detected</p>}
        {model.properties.length === 0 ? <div className="flex flex-1 items-center justify-center">
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              <IcoSettings24 className="text-tertiary" color="tertiary" />
              <div className="flex flex-col items-center justify-center gap-2">
                <p className="text-label-2 text-primary">No properties added yet</p>
                {emptyStateSubtitle != null && <p className="max-w-sm text-body-2 text-tertiary">{emptyStateSubtitle}</p>}
              </div>
            </div>
          </div> : <div className="flex flex-col gap-4">
            {model.properties.map((property_0, index_1) => <PropertyRow key={property_0.id} property={property_0} onChange={prop => handlePropertyChange(index_1, prop)} onDelete={() => handlePropertyDelete(index_1)} depth={0} error={rootDuplicateNames.has(property_0.name) ? 'Duplicate property name' : undefined} isDisabled={isDisabled} />)}
          </div>}
      </div>

      <div className="flex-shrink-0">
        <Button variant="secondary" size="compact" prefixIcon={IcoPlusSmall16} onClick={handleAddProperty} disabled={isDisabled}>
          Add property
        </Button>
      </div>
    </div>;
};
export default VisualEditor;